import type { ReactNode } from 'react';
import { PrismicNextLink } from '@prismicio/next';
import { isFilled, type LinkField } from '@prismicio/client';
import { AnchorCtaLink } from './AnchorCtaLink';
import { hasAnchor } from '@/utils/anchor-link-utils';

type Tone = 'dark' | 'light';

type Props = {
  field: LinkField;
  /**
   * Optional same-page target used **only when `field` has no destination**:
   * an element id (`lead-magnet-form`) or a slice type (`cta_with_side_form`).
   * Pass the raw Prismic value — normalization happens in `AnchorCtaLink`.
   * A filled link field always wins, so this can't change existing content.
   */
  anchor?: string | null;
  label?: string;
  tone?: Tone;
  stripTrailingArrow?: boolean;
  icon?: ReactNode;
  className?: string;
};

const TRAILING_ARROW_PATTERN = /\s*(→|->)\s*$/;

const TONE_CLASSES: Record<Tone, string> = {
  dark: 'border-white text-white hover:border-[#eaeaea] hover:bg-white/10',
  light: 'border-[#041118] text-[#041118]',
};

const buildClasses = (tone: Tone, className: string) =>
  `group inline-flex items-center justify-center gap-2 rounded-[8px] border bg-transparent px-6 py-3 text-[16px] font-medium leading-[1.5] transition-colors ${TONE_CLASSES[tone]} ${className}`;

/**
 * Reuse the OutlineCtaLink look on non-link elements (e.g. a <button>) so
 * variants that need the same visual as a link can share its styles without
 * duplicating the class string.
 */
export const getOutlineCtaClasses = (tone: Tone = 'light', className = '') =>
  buildClasses(tone, className);

const buildLabel = (
  field: LinkField,
  label: string | undefined,
  stripTrailingArrow: boolean,
) => {
  const text = label ?? (field as { text?: string }).text ?? '';
  return stripTrailingArrow ? text.replace(TRAILING_ARROW_PATTERN, '') : text;
};

const buildContent = (icon: ReactNode, displayText: string) => (
  <>
    {icon}
    <span className="group-hover:underline">{displayText}</span>
  </>
);

export function OutlineCtaLink({
  field,
  anchor,
  label,
  tone = 'light',
  stripTrailingArrow = false,
  icon,
  className = '',
}: Props) {
  const classes = buildClasses(tone, className);
  const content = buildContent(
    icon,
    buildLabel(field, label, stripTrailingArrow),
  );

  // The anchor is a fallback, never an override: it only applies when the link
  // field has no destination of its own. That keeps the feature additive — any
  // CTA an editor has already pointed somewhere behaves exactly as before.
  if (!isFilled.link(field) && hasAnchor(anchor)) {
    return (
      <AnchorCtaLink anchor={anchor} className={classes}>
        {content}
      </AnchorCtaLink>
    );
  }

  return (
    <PrismicNextLink field={field} className={classes}>
      {content}
    </PrismicNextLink>
  );
}
